import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChild,
  Input,
} from '@angular/core';
import { outputToObservable } from '@angular/core/rxjs-interop';
import { FormControlName } from '@angular/forms';

import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { IconHelpComponent } from '../../common/icon/icon-help/icon-help.component';
import { IconComponent } from '../../common/icon/icon.component';
import { LabelComponent } from '../../common/label/label.component';
import type { Variants } from '../../utils/variant/variant';
import { ComponentVariant } from '../../utils/variant/variant';
import { FormErrorDirective } from '../form-error/form-error.directive';
import { InputComponent } from '../input/input.component';

@Component({
  selector: 'rt-form-field',
  templateUrl: './form-field.component.html',
  styleUrls: ['./form-field.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.has-icon]': 'icon && !label',
    '[class.has-label]': 'label',
    '[class.has-errors]': 'errors.length > 0',
    '[class.is-forced]': 'isForced',
    '[class.is-focused]': 'isFocused',
    '[class.is-disabled]': 'isControlStatus("DISABLED")',
    '[class.no-label-margin]': '!hasLabelMargin',
    '[class]': 'variant',
  },
  imports: [],
})
export class FormFieldComponent {
  public static readonly VARIANTS: Variants = ['default', ['default', 'horizontal', 'rtl']];
  @ComponentVariant(FormFieldComponent.VARIANTS)
  @Input()
  public variant: string;
  public isForced: boolean;
  public errors: string[] = [];

  @Input() public hints: string[] = [];
  @Input() public hideRequiredBadge: boolean = false;
  @Input() public autoWatchOnFocusChange: boolean = true;
  @Input() public isFocused: boolean = false;
  @Input() public isValidationForced: boolean = false;
  @Input() public required: boolean = false;
  @Input() public displayFormControlErrors: boolean = false;
  @Input() public hasLabelMargin: boolean = true;

  @ContentChild(LabelComponent) public label: LabelComponent;
  @ContentChild(IconComponent) public icon: IconComponent;
  @ContentChild(FormErrorDirective) public errorDirective: FormErrorDirective;
  @ContentChild(FormControlName) public formControlCmp: FormControlName;
  @ContentChild(InputComponent) public inputCmp: InputComponent;
  @ContentChild(IconHelpComponent) public iconHelpCmp: IconHelpComponent;

  private ngOnDestroy$ = new Subject<void>();

  constructor(public cdRef: ChangeDetectorRef) {}

  public ngAfterViewInit(): void {
    if (this.inputCmp && this.autoWatchOnFocusChange) {
      outputToObservable(this.inputCmp.focus)
        .pipe(takeUntil(this.ngOnDestroy$.asObservable()))
        .subscribe(() => (this.isFocused = true));
      outputToObservable(this.inputCmp.blur)
        .pipe(takeUntil(this.ngOnDestroy$.asObservable()))
        .subscribe(() => (this.isFocused = false));
    }
    if (this.errorDirective) {
      this.errorDirective.error
        .pipe(takeUntil(this.ngOnDestroy$.asObservable()))
        .subscribe(payload => {
          this.errors = Array.isArray(payload.errors)
            ? payload.errors
            : Object.values(payload.errors);
          this.isValidationForced = payload.isForced;
          this.cdRef.detectChanges();
        });
    }

    if (this.formControlCmp) {
      this.formControlCmp.control.valueChanges
        .pipe(takeUntil(this.ngOnDestroy$.asObservable()))
        .subscribe(() => {
          this.isRequired();
          this.cdRef.detectChanges();
        });

      if (this.displayFormControlErrors) {
        this.formControlCmp
          .statusChanges!.pipe(takeUntil(this.ngOnDestroy$.asObservable()))
          .subscribe(() => {
            const { errors } = this.formControlCmp.control;
            this.errors = errors ? Object.values(errors) : [];
            this.cdRef.detectChanges();
          });
      }
    }
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public trackByError(index: number, error: string): string {
    return error;
  }

  public isRequired(): boolean {
    if (this.required) {
      return true;
    }

    if (
      this.formControlCmp &&
      this.formControlCmp.control &&
      this.formControlCmp.control.validator
    ) {
      const validators = this.formControlCmp.control.validator(this.formControlCmp.control);
      return validators ? validators['required'] || false : false;
    }

    return false;
  }

  public isControlStatus(statusPredicate: 'DISABLED'): boolean {
    if (this.formControlCmp) {
      return this.formControlCmp.control.status === statusPredicate;
    }

    return false;
  }
}
